/* eslint-disable react/no-danger */
import {
  formatLanguages,
  motherRestBetweenLittersOptions,
  pedigreeSelectOptions,
  useRouter,
} from '@application/utils';
import { CountryCode } from '@gql/graphql';
import type { MtBreederOnboardingPageProps } from '@presentation/pages';
import { useTranslation } from '@presentation/utils';
import { useMemo } from 'react';
import { FaRegEdit, FaDog } from 'react-icons/fa';
import { useFormikContext } from 'formik';
import { useTheme } from '@emotion/react';
import type { FC } from 'react';
import * as Styled from '../styled';
import { IMAGES } from '../assets';
import type { MtBreederOnboardingFormValues } from '../types';

export const Step13: FC<
  Pick<
    MtBreederOnboardingPageProps,
    | 'onEditClick'
    | 'hideAgreementNumberField'
    | 'breedingNameLabel'
    | 'breedOptions'
    | 'showCertificationFileField'
    | 'showGermanFileFields'
  >
> = (props) => {
  const {
    onEditClick,
    hideAgreementNumberField,
    breedingNameLabel,
    breedOptions,
    showGermanFileFields,
    showCertificationFileField,
  } = props;
  const { values } = useFormikContext<MtBreederOnboardingFormValues>();
  const { translate } = useTranslation();
  const { language } = useRouter();
  const { color } = useTheme();

  const breedNames = useMemo(() => {
    return breedOptions
      .filter((option) => values.dogBreedsIds?.includes(option.value))
      .map((option) => option.label)
      .join(', ');
  }, [breedOptions, values.dogBreedsIds]);

  const formattedMotherRest = useMemo(() => {
    const motherRestOptions = motherRestBetweenLittersOptions(translate);
    return (
      motherRestOptions.find((option) => option.value === values?.motherRestBetweenLitters)
        ?.label ?? values?.motherRestBetweenLitters
    );
  }, [translate, values?.motherRestBetweenLitters]);

  const formattedPedigree = useMemo(() => {
    const pedigreeOptions = pedigreeSelectOptions(translate, {
      showText: true,
      showIcons: false,
      value: values?.dogsPedigree,
    });
    return (
      pedigreeOptions.find((option) => option.value === values?.dogsPedigree)?.label ??
      values?.dogsPedigree
    );
  }, [translate, values?.dogsPedigree]);

  return (
    <Styled.Step13Container>
      <Styled.OnboardingDog src={IMAGES.onboardingLastDog} isEnd />
      <Styled.Step13Title>{translate('common.inputs.steps.reviewDetails')}</Styled.Step13Title>
      <Styled.Step13Subtitle>{translate('common.inputs.steps.checkAnswers')}</Styled.Step13Subtitle>
      <Styled.Step13SubContainer>
        <Styled.EditIcon onClick={() => onEditClick()}>
          <FaRegEdit size={20} color={color.medium.shade} />
        </Styled.EditIcon>
        <div>
          <Styled.Step13SubContainerTitle>
            {translate('common.inputs.spokenLanguages.label')}
          </Styled.Step13SubContainerTitle>
          <Styled.Step13SubContainerDesc>
            {formatLanguages(language, values?.spokenLanguages ?? ['fr'])}
          </Styled.Step13SubContainerDesc>
        </div>
        <div>
          <Styled.Step13SubContainerTitle>
            {translate('common.inputs.city.label')}
          </Styled.Step13SubContainerTitle>
          <Styled.Step13SubContainerDesc>{values?.city}</Styled.Step13SubContainerDesc>
        </div>
        <div>
          <Styled.Step13SubContainerTitle>
            {translate('common.inputs.zipcode.label')}
          </Styled.Step13SubContainerTitle>
          <Styled.Step13SubContainerDesc>{values?.zip}</Styled.Step13SubContainerDesc>
        </div>
        <div>
          <Styled.Step13SubContainerTitle>
            {translate('common.inputs.region.label')}
          </Styled.Step13SubContainerTitle>
          <Styled.Step13SubContainerDesc>
            {translate(`enum.regionCode.${values?.region}`, {
              defaultValue: null,
            })}
          </Styled.Step13SubContainerDesc>
        </div>
        <div>
          <Styled.Step13SubContainerTitle>
            {translate('common.inputs.country.label')}
          </Styled.Step13SubContainerTitle>
          <Styled.Step13SubContainerDesc>
            {translate(`enum.country.${values?.country}`)}
          </Styled.Step13SubContainerDesc>
        </div>
      </Styled.Step13SubContainer>
      {values?.country !== CountryCode.CH ? (
        <Styled.Step13SubContainer>
          <Styled.EditIcon onClick={() => onEditClick()}>
            <FaRegEdit size={20} color={color.medium.shade} />
          </Styled.EditIcon>
          <div>
            <Styled.Step13SubContainerTitle>
              {translate('common.inputs.breederType.label')}
            </Styled.Step13SubContainerTitle>
            <Styled.Step13SubContainerDesc>
              {translate(`common.options.breederTypes.${values?.level}`)}
            </Styled.Step13SubContainerDesc>
          </div>
          {!hideAgreementNumberField ? (
            <div>
              <Styled.Step13SubContainerTitle>
                {translate(
                  `common.inputs.agreementNumber.label.${values?.country ?? CountryCode.BE}`,
                )}
              </Styled.Step13SubContainerTitle>
              <Styled.Step13SubContainerDesc>
                {values?.agreementNumber}
              </Styled.Step13SubContainerDesc>
            </div>
          ) : null}
        </Styled.Step13SubContainer>
      ) : null}
      <Styled.Step13SubContainer>
        <Styled.EditIcon onClick={() => onEditClick()}>
          <FaRegEdit size={20} color={color.medium.shade} />
        </Styled.EditIcon>
        <div>
          <Styled.Step13SubContainerTitle>
            <span dangerouslySetInnerHTML={{ __html: breedingNameLabel }} />
          </Styled.Step13SubContainerTitle>
          <Styled.Step13SubContainerDesc>{values?.breedingName}</Styled.Step13SubContainerDesc>
        </div>
        <div>
          <Styled.Step13SubContainerTitle>
            {translate('common.inputs.raisedBreeds.label')}
          </Styled.Step13SubContainerTitle>
          <Styled.Step13SubContainerDesc>{breedNames}</Styled.Step13SubContainerDesc>
        </div>
        <div>
          <Styled.Step13SubContainerTitle>
            {translate('common.inputs.pedigree.label')}
          </Styled.Step13SubContainerTitle>
          <Styled.Step13SubContainerDesc>{formattedPedigree}</Styled.Step13SubContainerDesc>
        </div>
      </Styled.Step13SubContainer>
      <Styled.Step13SubContainer>
        <Styled.EditIcon onClick={() => onEditClick()}>
          <FaRegEdit size={20} color={color.medium.shade} />
        </Styled.EditIcon>
        <div>
          <Styled.Step13SubContainerTitle>
            {translate('common.inputs.websiteurl.website')}
          </Styled.Step13SubContainerTitle>
          <Styled.Step13SubContainerDesc>{values?.websiteUrl}</Styled.Step13SubContainerDesc>
        </div>
        <div>
          <Styled.Step13SubContainerTitle>
            {translate('common.inputs.websiteurl.facebook')}
          </Styled.Step13SubContainerTitle>
          <Styled.Step13SubContainerDesc>{values?.facebookUrl}</Styled.Step13SubContainerDesc>
        </div>
      </Styled.Step13SubContainer>
      <Styled.Step13SubContainer>
        <Styled.EditIcon onClick={() => onEditClick()}>
          <FaRegEdit size={20} color={color.medium.shade} />
        </Styled.EditIcon>
        <div>
          <Styled.Step13SubContainerTitle>
            {translate('common.inputs.breedingDesc.label')}
          </Styled.Step13SubContainerTitle>
          <Styled.Step13SubContainerDesc>
            {values?.translated ? values?.translated[language]?.description : ''}
          </Styled.Step13SubContainerDesc>
        </div>
      </Styled.Step13SubContainer>
      <Styled.Step13SubContainer>
        <Styled.EditIcon onClick={() => onEditClick()}>
          <FaRegEdit size={20} color={color.medium.shade} />
        </Styled.EditIcon>
        <div>
          <Styled.Step13SubContainerTitle>
            {translate('common.inputs.femaleBreedersCount.label')}
          </Styled.Step13SubContainerTitle>
          <Styled.Step13SubContainerDesc>
            {values?.femaleBreedersCount}
          </Styled.Step13SubContainerDesc>
        </div>
        <div>
          <Styled.Step13SubContainerTitle>
            {translate('common.inputs.breedFemales.label')}
          </Styled.Step13SubContainerTitle>
          <Styled.Step13SubContainerDesc>{formattedMotherRest}</Styled.Step13SubContainerDesc>
        </div>
        <div>
          <Styled.Step13SubContainerTitle>
            {translate('common.inputs.littersByFemale.label')}
          </Styled.Step13SubContainerTitle>
          <Styled.Step13SubContainerDesc>
            {values?.littersByFemale ? values?.littersByFemale : ''}
          </Styled.Step13SubContainerDesc>
        </div>
      </Styled.Step13SubContainer>
      <Styled.Step13SubContainer>
        <Styled.EditIcon onClick={() => onEditClick()}>
          <FaRegEdit size={20} color={color.medium.shade} />
        </Styled.EditIcon>
        <div>
          <Styled.Step13SubContainerTitle>
            {translate('common.inputs.diseaseTesting.label').replaceAll('<br/>', '')}
          </Styled.Step13SubContainerTitle>
          <Styled.Step13SubContainerDesc>
            {values?.medicalTestsPerformed}
          </Styled.Step13SubContainerDesc>
        </div>
        <div>
          <Styled.Step13SubContainerTitle>
            {translate('common.inputs.breedsConsanguinity.label').replaceAll('<br/>', '')}
          </Styled.Step13SubContainerTitle>
          <Styled.Step13SubContainerDesc>{values?.dogsConsanguinity}</Styled.Step13SubContainerDesc>
        </div>
      </Styled.Step13SubContainer>
      <Styled.Step13SubContainer>
        <Styled.EditIcon onClick={() => onEditClick()}>
          <FaRegEdit size={20} color={color.medium.shade} />
        </Styled.EditIcon>
        <div>
          <Styled.Step13SubContainerTitle>
            {translate('common.inputs.veterinaryInfo.label').replaceAll('<br/>', '')}
          </Styled.Step13SubContainerTitle>
          <Styled.Step13SubContainerDesc>{values?.veterinaryInfo}</Styled.Step13SubContainerDesc>
        </div>
      </Styled.Step13SubContainer>
      <Styled.Step13SubContainer>
        <Styled.EditIcon onClick={() => onEditClick()}>
          <FaRegEdit size={20} color={color.medium.shade} />
        </Styled.EditIcon>
        <div>
          <Styled.Step13SubContainerTitle>
            {translate('common.inputs.dogsLivingPlace.label')}
          </Styled.Step13SubContainerTitle>
          <Styled.Step13SubContainerDesc>
            {values?.translated?.[language]?.dogsLivingPlace}
          </Styled.Step13SubContainerDesc>
        </div>
        <div>
          <Styled.Step13SubContainerTitle>
            {translate('common.inputs.puppiesLivingPlace.label')}
          </Styled.Step13SubContainerTitle>
          <Styled.Step13SubContainerDesc>
            {values?.translated?.[language]?.puppiesLivingPlace}
          </Styled.Step13SubContainerDesc>
        </div>
      </Styled.Step13SubContainer>
      <Styled.Step13SubContainer>
        <Styled.EditIcon onClick={() => onEditClick()}>
          <FaRegEdit size={20} color={color.medium.shade} />
        </Styled.EditIcon>
        <div>
          <Styled.Step13SubContainerTitle>
            {translate('common.inputs.breederDogsSelection.label')}
          </Styled.Step13SubContainerTitle>
          <Styled.Step13SubContainerDesc>
            {values?.translated?.[language]?.breederDogsSelection}
          </Styled.Step13SubContainerDesc>
        </div>
        <div>
          <Styled.Step13SubContainerTitle>
            {translate('common.inputs.puppiesTraining.label')}
          </Styled.Step13SubContainerTitle>
          <Styled.Step13SubContainerDesc>
            {values?.translated?.[language]?.puppiesTraining}
          </Styled.Step13SubContainerDesc>
        </div>
      </Styled.Step13SubContainer>
      <Styled.Step13ImageSection>
        <Styled.EditIcon onClick={() => onEditClick()}>
          <FaRegEdit size={20} color={color.medium.shade} />
        </Styled.EditIcon>
        <Styled.Step13ImageSubsection>
          <Styled.Step13ImageType>
            {translate('common.inputs.profilePicture.label')}
          </Styled.Step13ImageType>
          {values?.profileImage ? (
            <Styled.MainImage src={values?.profileImage as string} />
          ) : (
            <Styled.Skeleton>
              <FaDog size={28} color={color.blue.default} />
            </Styled.Skeleton>
          )}
          <Styled.Step13ImageType>
            {translate('common.inputs.breederPictures.label')}*
          </Styled.Step13ImageType>
          <Styled.Step13Kennel>
            {values?.images?.map((img) => (
              <Styled.MainImage src={img as string} key={img as string} />
            ))}
          </Styled.Step13Kennel>
        </Styled.Step13ImageSubsection>
      </Styled.Step13ImageSection>
      <Styled.Step13DocSection>
        <Styled.EditIcon onClick={() => onEditClick()}>
          <FaRegEdit size={20} color={color.medium.shade} />
        </Styled.EditIcon>

        {showGermanFileFields ? (
          <>
            <Styled.DocContainer>
              <Styled.DocTextsContainer>
                <Styled.Picture>
                  {translate('mt.breeder.onboarding.permissionFiles.label')}
                </Styled.Picture>
              </Styled.DocTextsContainer>

              {values?.permissionFiles?.length ? (
                <Styled.MultiDocumentWrapper>
                  {values.permissionFiles.map((file, index) => (
                    <Styled.Document
                      multi
                      target="_blank"
                      href={file as string}
                      key={file as string}
                    >
                      {`File ${index + 1}`}
                    </Styled.Document>
                  ))}
                </Styled.MultiDocumentWrapper>
              ) : (
                <Styled.NoFileLabel>{translate('common.noFiles')}</Styled.NoFileLabel>
              )}
            </Styled.DocContainer>

            <Styled.DocContainer>
              <Styled.DocTextsContainer>
                <Styled.Picture>
                  {translate('mt.breeder.onboarding.identityFiles.label')}{' '}
                </Styled.Picture>
              </Styled.DocTextsContainer>

              {values?.identityFiles?.length ? (
                <Styled.MultiDocumentWrapper>
                  {values.identityFiles.map((file, index) => (
                    <Styled.Document
                      target="_blank"
                      href={file as string}
                      key={file as string}
                      multi
                    >
                      {`File ${index + 1}`}
                    </Styled.Document>
                  ))}
                </Styled.MultiDocumentWrapper>
              ) : (
                <Styled.NoFileLabel>{translate('common.noFiles')}</Styled.NoFileLabel>
              )}
            </Styled.DocContainer>
          </>
        ) : (
          <Styled.DocContainer>
            <Styled.DocTextsContainer>
              <Styled.Picture>
                {translate(`mt.breeder.onboarding.agreementFile.label.${values?.country}`)}
              </Styled.Picture>
            </Styled.DocTextsContainer>
            {values?.hkFile ? (
              <Styled.Document target="_blank" href={values?.hkFile as string}>
                {translate(`mt.breeder.onboarding.agreementFile.label.${values?.country}`)}
              </Styled.Document>
            ) : (
              <Styled.NoFileLabel>{translate('common.noFiles')}</Styled.NoFileLabel>
            )}
          </Styled.DocContainer>
        )}

        <Styled.DocContainer>
          <Styled.DocTextsContainer>
            <Styled.Picture>
              {translate('mt.breeder.onboarding.breedClubFiles.label')}
            </Styled.Picture>
          </Styled.DocTextsContainer>

          {values?.breedClubFiles?.length ? (
            <Styled.MultiDocumentWrapper>
              {values.breedClubFiles.map((file, index) => (
                <Styled.Document multi target="_blank" href={file as string} key={file as string}>
                  {`File ${index + 1}`}
                </Styled.Document>
              ))}
            </Styled.MultiDocumentWrapper>
          ) : (
            <Styled.NoFileLabel>{translate('common.noFiles')}</Styled.NoFileLabel>
          )}
        </Styled.DocContainer>

        {showCertificationFileField && (
          <Styled.DocContainer>
            <Styled.DocTextsContainer>
              <Styled.Picture>
                {translate('mt.breeder.onboarding.certificationFile.label')}
              </Styled.Picture>
            </Styled.DocTextsContainer>
            {values?.certificationFile ? (
              <Styled.Document target="_blank" href={values.certificationFile as string}>
                {translate('glossary.certificationFile')}
              </Styled.Document>
            ) : (
              <Styled.NoFileLabel>{translate('common.noFiles')}</Styled.NoFileLabel>
            )}
          </Styled.DocContainer>
        )}
      </Styled.Step13DocSection>
    </Styled.Step13Container>
  );
};
